import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Avatar,
  InputAdornment,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { uploadsUrl } from '../../utils/api';
import PageHeader from '../../components/Layout/PageHeader';
import MemberAutoApproveCard from '../../components/MemberAutoApproveCard';
import SystemNoticeSettingsCard from '../../components/SystemNoticeSettingsCard';
import { surfaceCardSx, tokens } from '../../theme/tokens';

export default function AdminProfile() {
  const { staff, refreshStaff } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country: '',
    region: '',
    city: '',
    residential_address: '',
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);

  useEffect(() => {
    if (staff) {
      setFormData({
        first_name: staff.first_name || '',
        last_name: staff.last_name || '',
        email: staff.email || '',
        phone: staff.phone || '',
        country: staff.country || '',
        region: staff.region || '',
        city: staff.city || '',
        residential_address: staff.residential_address || '',
      });
    }
  }, [staff]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileImageFile(file);
      const reader = new FileReader();
      reader.onload = () => setProfileImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleProfileImageUpload = async () => {
    if (!profileImageFile) return;
    setError('');
    setSuccess('');
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('profile_image', profileImageFile);
      const res = await api.post('/auth/profile/picture', formData);
      setSuccess(res.data.message || 'Profile picture updated');
      if (res.data.staff) refreshStaff();
      setProfileImageFile(null);
      setProfileImagePreview(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload profile picture');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const res = await api.put('/auth/profile', formData);
      setSuccess(res.data.message || 'Profile updated successfully');
      if (res.data.staff) {
        refreshStaff();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New password and confirmation do not match');
      return;
    }
    if (passwordData.new_password.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await api.put('/auth/password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      setSuccess('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (!staff) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        crumbs={<>System / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Settings</Box></>}
        title="Settings"
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: '9px', bgcolor: tokens.dangerSoft, color: '#7C2E1D', border: '1px solid #EFCFC5' }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: '9px' }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ ...surfaceCardSx, overflow: 'hidden', mb: { xs: 0, md: 0 } }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${tokens.border}` }}>
              <Typography sx={{ fontFamily: tokens.fontDisplay, fontWeight: 600, fontSize: 15 }}>
                Profile & organisation
              </Typography>
            </Box>
            <Box sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Avatar
                  src={profileImagePreview || (staff.profile_image ? uploadsUrl(staff.profile_image) : null)}
                  sx={{ width: 64, height: 64, bgcolor: tokens.primary, fontWeight: 600 }}
                >
                  {staff.first_name?.[0] || 'U'}
                </Avatar>
                <Box flex={1}>
                  <Typography sx={{ fontWeight: 600, fontFamily: tokens.fontDisplay }}>
                    {staff.first_name} {staff.last_name}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: tokens.muted }}>
                    {staff.email}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      component="label"
                      variant="outlined"
                      size="small"
                      disabled={uploadingImage}
                      sx={{ borderColor: tokens.border, color: tokens.ink }}
                    >
                      {profileImageFile ? 'Change' : 'Upload'} photo
                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/jpg,image/png,image/gif"
                        onChange={handleProfileImageChange}
                      />
                    </Button>
                    {profileImageFile && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleProfileImageUpload}
                        disabled={uploadingImage}
                        sx={{ bgcolor: tokens.primary, '&:hover': { bgcolor: tokens.primaryStrong } }}
                      >
                        {uploadingImage ? 'Uploading...' : 'Save photo'}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Region"
                      name="region"
                      value={formData.region}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Residential Address"
                      name="residential_address"
                      value={formData.residential_address}
                      onChange={handleChange}
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                    sx={{ bgcolor: tokens.primary, '&:hover': { bgcolor: tokens.primaryStrong } }}
                  >
                    {saving && <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />}
                    {saving ? 'Saving...' : 'Save changes'}
                  </Button>
                </Box>
              </form>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ ...surfaceCardSx, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${tokens.border}` }}>
              <Typography sx={{ fontFamily: tokens.fontDisplay, fontWeight: 600, fontSize: 15 }}>
                Security
              </Typography>
            </Box>
            <Box sx={{ p: 2.5 }}>
              <Typography sx={{ fontSize: 13.5, color: tokens.muted, mb: 2 }}>
                Update your password to keep your account secure.
              </Typography>
              <form onSubmit={handlePasswordSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Current Password"
                      name="current_password"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={handlePasswordChange}
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              edge="end"
                              size="small"
                            >
                              {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="New Password"
                      name="new_password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.new_password}
                      onChange={handlePasswordChange}
                      required
                      helperText="At least 6 characters"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              edge="end"
                              size="small"
                            >
                              {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      name="confirm_password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordData.confirm_password}
                      onChange={handlePasswordChange}
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              size="small"
                            >
                              {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3 }}>
                  <Button
                    type="submit"
                    variant="outlined"
                    disabled={changingPassword || !passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password}
                    sx={{ borderColor: tokens.border, color: tokens.ink }}
                  >
                    {changingPassword && <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />}
                    {changingPassword ? 'Changing...' : 'Change password'}
                  </Button>
                </Box>
              </form>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ ...surfaceCardSx, p: 2.5, mb: 2 }}>
            <Typography sx={{ fontFamily: tokens.fontDisplay, fontWeight: 600, fontSize: 15, mb: 1.5 }}>
              Account hardening
            </Typography>
            <Box sx={{ fontSize: 13.5, color: tokens.muted, display: 'grid', gap: 1.1 }}>
              <Box>✓ Passwords hashed with bcrypt</Box>
              <Box>✓ Role-based access on every API route</Box>
              <Box>✓ Audit log enabled</Box>
            </Box>
          </Paper>

          <MemberAutoApproveCard
            sx={{
              mb: 2,
              borderRadius: tokens.radius,
              boxShadow: tokens.shadow,
              borderColor: tokens.border,
            }}
          />
          <SystemNoticeSettingsCard
            sx={{
              mb: 0,
              borderRadius: tokens.radius,
              boxShadow: tokens.shadow,
              borderColor: tokens.border,
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
